<template>
  <!--
    Панель настроек. Открывается поверх бокового меню и своим закрытием его не
    трогает: пришли из меню — вернёмся в меню, пришли с дашборда — на дашборд.
    Поэтому состоянием меню отсюда не управляем вовсе.

    Подложка закрывает панель по клику. Клавиатурный путь у неё есть, но другой —
    Escape, он висит на документе: вешать обработчик клавиш на саму подложку
    бессмысленно, фокус на ней никогда не окажется.
  -->
  <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
  <aside
    class="settings-aside"
    @click.self="close"
  >
    <div class="settings-panel">
      <!-- Своей шапки у панели нет: заголовок и крестик живут в шапке
           приложения, она выше по z-index и рисуется поверх. -->
      <!-- Обёртка задаёт высоту: у .upCard глобально height: 100%, и без
           родителя с высотой по содержимому карточка растянется на всю панель. -->
      <!-- Прячем карточку целиком, а не одну строку: пустая карточка осталась
           бы на месте отступом. -->
      <div
        v-if="showLangSwitch"
        class="settings-card"
      >
        <up-card>
          <div class="settings-row">
            <span class="settings-row__label">{{ $t('settings.language') }}</span>
            <div class="settings-row__control">
              <up-button @click="changeLocale">
                {{ otherLangLabel }}
              </up-button>
            </div>
          </div>
        </up-card>
      </div>

      <div class="settings-card">
        <up-card>
          <div class="settings-row">
            <span class="settings-row__label">{{ $t('settings.notifications') }}</span>
            <div class="settings-row__control">
              <up-button
                :disabled="pushBusy || !pushActionable"
                @click="togglePush"
              >
                {{ pushButtonLabel }}
              </up-button>
            </div>
          </div>

          <p
            v-if="pushHint"
            class="settings-hint"
          >
            {{ pushHint }}
            <button
              v-if="pushState === 'denied'"
              type="button"
              class="settings-hint__link"
              @click="howToAsked = true"
            >
              {{ $t('settings.push_howto') }}
            </button>
          </p>
        </up-card>
      </div>

      <!-- Поддержка — то, что диктует оператор, когда у человека «всё залипло».
           Свёрнута, чтобы в сброс не тыкали из любопытства: сначала раскрыть,
           потом нажать, потом подтвердить. Стоит внизу, рядом с выходом, —
           там же, где прочие действия, после которых приложение уже не то. -->
      <!-- Свёрнутую карточку раскрывает нажатие в любое её место, а не только
           в заголовок. Клавиатурный путь — сам заголовок, он кнопка. -->
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
      <div
        class="settings-card settings-card--bottom"
        :class="{ 'settings-card--clickable': !supportOpen }"
        @click="supportOpen = true"
      >
        <up-card>
          <!-- Заголовок целиком — кнопка: попасть в него проще, чем в стрелку.
               Справа нарисована кнопка как у соседних строк, иначе строка не
               читалась как нажимаемая. Настоящей кнопкой её не сделать —
               кнопку в кнопку не вложить, — поэтому это только оформление.
               stop — чтобы сворачивание не отменил обработчик карточки. -->
          <button
            type="button"
            class="settings-row settings-section"
            :aria-expanded="supportOpen"
            @click.stop="supportOpen = !supportOpen"
          >
            <span class="settings-row__label">{{ $t('settings.support') }}</span>
            <span class="settings-section__toggle">
              <i :class="supportOpen ? 'exu-chevron-up' : 'exu-chevron-down'" />
            </span>
          </button>

          <!-- Содержимое отчёркнуто от заголовка линией: иначе заголовок ничем
               не отличался от строк под ним. Жирным его не выделяем — жирного
               в панели больше нет нигде. -->
          <div
            v-if="supportOpen"
            class="settings-support"
          >
            <div class="settings-row">
              <span class="settings-row__label">
                {{ $t('settings.version', { version: appVersion }) }}
              </span>
              <div class="settings-row__control">
                <up-button
                  :disabled="updateBusy"
                  @click="onUpdateClick"
                >
                  {{ updateButtonLabel }}
                </up-button>
              </div>
            </div>

            <p
              v-if="updateHint"
              class="settings-hint settings-hint--tight"
            >
              {{ updateHint }}
            </p>

            <div class="settings-row settings-row--spaced">
              <span class="settings-row__label">{{ $t('settings.reset') }}</span>
              <div class="settings-row__control">
                <up-button @click="askReset">
                  {{ $t('settings.reset_action') }}
                </up-button>
              </div>
            </div>

            <!-- Вплотную к строке сброса: по близости и читается, что
                 подсказка про сброс, а не про весь раздел. -->
            <p class="settings-hint settings-hint--tight">{{ $t('settings.reset_hint') }}</p>
          </div>
        </up-card>
      </div>

      <div
        v-if="authorized"
        class="settings-card"
      >
        <up-card>
          <div class="settings-row">
            <span class="settings-row__label">{{ $t('settings.account') }}</span>
            <div class="settings-row__control">
              <up-button @click="askLogout">
                {{ $t('settings.logout') }} <i class="exu-log-out" />
              </up-button>
            </div>
          </div>
        </up-card>
      </div>
    </div>

    <popup-card
      v-if="howToAsked"
      :is-open="howToAsked"
      @show="howToAsked = false"
    >
      <div class="settings-confirm">
        <p class="settings-confirm__text">{{ $t('settings.push_howto_text') }}</p>
      </div>
    </popup-card>

    <popup-card
      v-if="logoutAsked"
      :is-open="logoutAsked"
      @show="logoutAsked = false"
    >
      <div class="settings-confirm">
        <p class="settings-confirm__text">{{ $t('settings.logout_question') }}</p>
        <div class="settings-confirm__actions">
          <up-button @click="logoutAsked = false">
            {{ $t('settings.cancel') }}
          </up-button>
          <up-button
            :disabled="loggingOut"
            @click="confirmLogout"
          >
            {{ $t('settings.logout') }}
          </up-button>
        </div>
      </div>
    </popup-card>

    <popup-card
      v-if="resetAsked"
      :is-open="resetAsked"
      @show="resetAsked = false"
    >
      <div class="settings-confirm">
        <p class="settings-confirm__text">{{ $t('settings.reset_question') }}</p>
        <p class="settings-confirm__text">{{ $t('settings.reset_warning') }}</p>
        <p
          v-if="resetOffline"
          class="settings-confirm__text settings-confirm__text--alert"
        >
          {{ $t('settings.reset_offline') }}
        </p>
        <div class="settings-confirm__actions">
          <up-button @click="resetAsked = false">
            {{ $t('settings.cancel') }}
          </up-button>
          <up-button
            :disabled="resetting || resetOffline"
            @click="confirmReset"
          >
            {{ $t('settings.reset_action') }}
          </up-button>
        </div>
      </div>
    </popup-card>
  </aside>
</template>

<script>
import push from '@/components/mixins/methodsPush';
import { applyUpdate, checkForUpdate, hardReset } from '@/components/mixins/appMaintenance';
import { APP_VERSION } from '@/appVersion';

/** Сколько сброс ждёт отписки от push, прежде чем стирать без неё. */
const RESET_PUSH_TIMEOUT = 5000;

export default {
  name: 'settingsAside',
  mixins: [push],
  props: {
    locale: {
      type: String,
      default: 'ru',
    },
  },
  data() {
    return {
      logoutAsked: false,
      loggingOut: false,
      howToAsked: false,
      pushBusy: false,
      pushPermissionState: 'default',
      pushSubscribed: false,
      // Каждое открытие панели начинается со свёрнутой поддержки: панель
      // создаётся заново, и раскрытость с прошлого раза не переживает.
      supportOpen: false,
      // Пусто — ещё не проверяли; дальше статусы checkForUpdate и 'checking'.
      updateStatus: '',
      updateRegistration: null,
      resetAsked: false,
      resetOffline: false,
      resetting: false,
    };
  },
  computed: {
    // Справочник языков мероприятия приходит в конфиге. Один язык — переключать
    // не на что, прячем. Пустой справочник значит, что конфиг про языки ничего
    // не сказал: показываем, как показывали до появления параметра.
    showLangSwitch() {
      return Object.keys(this.$store.getters.getLanguageList).length !== 1;
    },
    // Кнопка называется языком, на который переключит, а не текущим.
    // Так было и у прежней кнопки в меню.
    otherLangLabel() {
      return this.locale === 'ru' ? 'Eng' : 'Рус';
    },
    authorized() {
      return !!this.$store.getters.getToken;
    },
    /**
     * Четыре состояния, а не два.
     *
     * unsupported — браузер не умеет push вовсе. Это весь iOS, пока приложение
     *               не установлено на домашний экран.
     * denied      — человек уже отказал в системном окне. Необратимо из кода:
     *               второй раз браузер спросить не даст никому.
     * on / off    — подписан или нет.
     */
    pushState() {
      if (!this.pushSupported()) return 'unsupported';
      if (this.pushPermissionState === 'denied') return 'denied';

      return this.pushSubscribed ? 'on' : 'off';
    },
    pushActionable() {
      return this.pushState === 'on' || this.pushState === 'off';
    },
    pushButtonLabel() {
      return this.pushState === 'on'
        ? this.$t('settings.push_disable')
        : this.$t('settings.push_enable');
    },
    pushHint() {
      if (this.pushState === 'unsupported') return this.$t('settings.push_unsupported');
      if (this.pushState === 'denied') return this.$t('settings.push_blocked');

      return '';
    },
    appVersion() {
      return APP_VERSION;
    },
    updateBusy() {
      return this.updateStatus === 'checking' || this.updateStatus === 'applying';
    },
    // Нашлась готовая версия — та же кнопка её и ставит, второй кнопки не заводим.
    updateButtonLabel() {
      return this.updateStatus === 'available'
        ? this.$t('settings.update_apply')
        : this.$t('settings.update_check');
    },
    updateHint() {
      const hints = {
        checking: 'settings.update_checking',
        available: 'settings.update_available',
        downloading: 'settings.update_downloading',
        latest: 'settings.update_latest',
        unsupported: 'settings.update_unsupported',
        failed: 'settings.update_failed',
      };

      return hints[this.updateStatus] ? this.$t(hints[this.updateStatus]) : '';
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    this.refreshPushState();
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    },
    changeLocale() {
      this.$emit('switch-locale', this.locale === 'ru' ? 'en' : 'ru');
    },
    refreshPushState() {
      this.pushPermissionState = this.pushPermission();

      return this.currentPushSubscription()
        .then((subscription) => {
          this.pushSubscribed = !!subscription;
        });
    },
    togglePush() {
      if (!this.pushActionable) return Promise.resolve();

      this.pushBusy = true;

      const action = this.pushState === 'on'
        ? this.unsubscribePush()
        // Разрешение спрашиваем прямо здесь: это обработчик нажатия, и только
        // из него браузер согласен показать своё окно.
        : this.requestPushPermission().then((permission) => {
          this.pushPermissionState = permission;

          return permission === 'granted' ? this.subscribePush() : false;
        });

      return action
        .then(() => this.refreshPushState())
        .finally(() => {
          this.pushBusy = false;
        });
    },
    askLogout() {
      this.logoutAsked = true;
    },
    // Сам выход делает App.vue: там и роутер, и миксин входа. Панель только
    // спрашивает и блокирует кнопку, пока снимается подписка.
    confirmLogout() {
      this.loggingOut = true;
      this.$emit('logout');
    },
    onUpdateClick() {
      if (this.updateStatus === 'available') {
        // Перезагрузит обработчик controllerchange в App.vue.
        this.updateStatus = 'applying';
        applyUpdate(this.updateRegistration);
        return Promise.resolve();
      }

      this.updateStatus = 'checking';

      return checkForUpdate()
        .then(({ status, registration = null }) => {
          this.updateStatus = status;
          this.updateRegistration = registration;
        });
    },
    // Без сети сброс оставит человека с пустым экраном: загрузиться обратно
    // приложению будет не из чего. Поэтому смотрим сеть в момент вопроса.
    askReset() {
      this.resetOffline = !navigator.onLine;
      this.resetAsked = true;
    },
    // Подписку снимаем первой: для запроса к бэку нужен ещё не стёртый exhID,
    // а снятие service worker её всё равно убьёт, только бэк об этом не узнает.
    // Отказ бэка сброс не останавливает — сброс и нужен, когда всё сломано.
    confirmReset() {
      this.resetting = true;

      // На плохой сети запрос к бэку может висеть долго, а человек смотрит
      // на заблокированную кнопку. Ждём ограниченно.
      const pushTimeout = new Promise((resolve) => { setTimeout(resolve, RESET_PUSH_TIMEOUT); });

      return Promise.race([this.unsubscribePush().catch(() => false), pushTimeout])
        .then(() => hardReset());
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/style/variables.scss';

// Панель меню лежит на z-index 200, эта должна быть выше.
.settings-aside {
  z-index: 210;
}

.settings-panel {
  display: flex;
  flex-direction: column;
  gap: $padding-step * 3;
  width: 100%;
  max-width: 420px;
  height: 100%;
  padding: $padding-step * 3;
  overflow-y: auto;
  background: $color-base-secondary;
}

// Высота по содержимому. Карточка внутри тянется на 100% уже от неё.
.settings-card {
  flex: 0 0 auto;
}

// Поддержка и выход всегда внизу. Свободное место забирает верхний отступ
// первой из них, поддержки, — выход идёт следом. Пока настройки помещаются на
// экран, обе стоят внизу, а когда их станет больше, просто уедут за границу —
// тогда до них надо будет доскроллить.
.settings-card--bottom {
  margin-top: auto;
}

.settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $padding-step * 3;
}

.settings-row__label {
  color: $color-text;
}

.settings-card--clickable {
  cursor: pointer;
}

// Кнопка-заголовок выглядит обычной строкой: подпись слева, кнопка справа.
.settings-section {
  width: 100%;
  padding: 0;
  font: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

// Рисованная кнопка: те же рамка, отступы и ширина, что у up-button в
// соседних строках, — её стили scoped, класс оттуда не взять. Блок, а не
// flex: так высоту задаёт строка текста, как у настоящей кнопки, а не иконка.
.settings-section__toggle {
  display: block;
  flex: 0 0 auto;
  min-width: 92px;
  text-align: center;
  padding: $padding-step $padding-step * 3;
  color: $color-primary;
  background: $color-base;
  border: 2px solid $color-primary;
  border-radius: $border-radius-inside-large;
}

// Отступа сверху нет: промежуток до заголовка уже даёт up-card, между её
// детьми gap 15px.
.settings-support {
  padding-top: $padding-step * 4;
  border-top: 1px solid $color-border;
}

.settings-row--spaced {
  margin-top: $padding-step * 4;
}

.settings-hint {
  margin: $padding-step * 2 0 0;
  font-size: 13px;
  color: $color-disabled;
}

// Подсказка к строке над ней. Отступ до строки заметно меньше, чем между
// строками, — так и видно, к какой строке она относится.
.settings-hint--tight {
  margin-top: $padding-step;
}

.settings-hint__link {
  padding: 0;
  font: inherit;
  color: $color-primary;
  text-decoration: underline;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.settings-confirm {
  display: flex;
  flex-direction: column;
  gap: $padding-step * 4;
}

.settings-confirm__text {
  margin: 0;
  color: $color-text;
}

.settings-confirm__text--alert {
  color: $color-accent-strong;
}

.settings-confirm__actions {
  display: flex;
  gap: $padding-step * 3;

  :deep(.up-btn) {
    border-radius: $border-radius-inside-large;
  }
}

// Кнопка растягивается на всю ширину родителя — ширину задаёт обёртка,
// а не переопределение стилей самой кнопки.
// Ширина по содержимому, но не уже соседей: кнопки в разных строках должны
// выглядеть одним рядом, а «Выйти» с иконкой шире, чем «Eng».
.settings-row__control {
  flex: 0 0 auto;
  min-width: 92px;

  // Прямые углы кнопки спорят с сильным скруглением карточки.
  :deep(.up-btn) {
    gap: $padding-step * 2;
    padding: $padding-step $padding-step * 3;
    border-radius: $border-radius-inside-large;
  }
}
</style>
